import { STAND_BEHIND, STAND_FRONT, standMarks } from "../compile/stand-marks.ts";
import { STAND_REACH } from "../compile/stand-cells.ts";
import { footprintCorner } from "../compile/footprint.ts";
import { COMPILED_COLLISION_CELLS_PER_TILE } from "../document/types.ts";

/**
 * Where a character standing beside one prop is drawn (ADR-132 decisions 3 and 4), for the footprint panel's stand-in:
 * the answer of the game's own `standMarks` for the one prop on a map just large enough to hold it and the cells a
 * builder may paint around it, and the base line's rule where no cell is painted.
 */

/**
 * The frame the panel paints: its size in pixels, the row its art meets the ground counted from the top, the side in
 * pixels of the cells the marks are counted in and the marks, `[column, rowsUp]`.
 */
export interface StandHereFrame {
  readonly w: number;
  readonly h: number;
  readonly base: number;
  readonly cell: number;
  readonly behind: readonly (readonly [number, number])[];
  readonly front: readonly (readonly [number, number])[];
}

/**
 * Which side of a prop a character is drawn.
 */
export type StandSide = "behind" | "front";

/**
 * Whether a character whose feet stand on a point of the frame is drawn behind the prop or in front of it.
 *
 * @param frame The frame and its marks.
 * @param feet The feet point in frame pixels, x from the frame's left edge and y up from its bottom edge; it may lie
 *   outside the frame.
 * @param tileSize Tile size in pixels.
 * @returns `behind` or `front`: the painted cell under the feet decides, and otherwise the base line, feet on or above it
 *   behind and feet below it in front.
 */
export function standSideHere(frame: StandHereFrame, feet: { readonly x: number; readonly y: number }, tileSize: number): StandSide {
  const px = tileSize / COMPILED_COLLISION_CELLS_PER_TILE;
  const pad = (STAND_REACH + 1) * Math.max(frame.cell, px);
  const tilesLeft = Math.ceil(pad / tileSize) + 1;
  const tilesUp = Math.ceil((frame.h + pad) / tileSize) + 1;
  const width = tilesLeft + Math.ceil((frame.w + pad) / tileSize) + 1;
  const height = tilesUp + Math.ceil(pad / tileSize) + 1;
  const placement = { frame: "here", x: tilesLeft, y: tilesUp - 1, flipX: false, flipY: false, sortPixels: 0, depth: 0 };
  const lookup = standMarks([placement], () => frame, tileSize, width, height);
  const corner = footprintCorner(tilesLeft * tileSize, tilesUp * tileSize, frame.w, false, px);
  const cx = Math.floor((corner.left + feet.x) / px);
  const cy = Math.floor((corner.bottom - feet.y - 1) / px);
  const side = lookup.sideAt(cx, cy);
  if (side === STAND_BEHIND) {
    return "behind";
  }
  if (side === STAND_FRONT) {
    return "front";
  }
  return feet.y >= frame.h - frame.base ? "behind" : "front";
}
